import {
  Box,
  Drawer,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { KeyboardDoubleArrowRight as ChevronDoubleRightIcon } from '@mui/icons-material'
import { useEffect, useState, type ReactNode } from 'react'

import { Sidebar } from '@/apps/latest/components/organisms/Sidebar/Sidebar'
import { TopBar } from '@/apps/latest/components/organisms/TopBar'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { useAnyCompassEmbed } from '@/features/anycompass'
import { LAYOUT } from '@/shared/styles/spacing'
import { ICON_SIZE } from '@/shared/styles/typography'

// 左ペインを描く側が受け取るもの。Layout は左ペインを Drawer (スマホ幅 / 埋め込み) と
// 固定列の 2 か所で描き、閉じ方が違う (Drawer は遷移でも閉じる) ので、要素でなく関数で受ける。
export interface SidebarRenderProps {
  onToggle?: () => void
  onNavigate?: () => void
}

interface LayoutProps {
  children: ReactNode
  breadcrumb?: string[]
  /**
   * 左ペインの差し替え。省略時は薬剤師の `Sidebar` (最近の薬歴・設定)。
   * Layout 自身は呼び出し側の系統を知らず、開閉と置き場所だけを持つ。
   */
  renderSidebar?: (props: SidebarRenderProps) => ReactNode
  /**
   * 本文幅を `LAYOUT.contentMaxWide` (= 100%) にする意図的例外.
   * 既定の 720px は「読む」ページの寸法で、 列の多い表やチャートを主役にする画面では
   * 横スクロールを生むだけになる (= 1920px 幅で 900px 超が余白のまま残る).
   * 使用箇所は design-book 00-principles §骨格 の定めどおり 04-apps/kakeru.md に理由付きで記録する.
   */
  wide?: boolean
}

const SIDEBAR_OPEN_KEY = 'kakeru.sidebar.open'

// 28 spec §2.1 アプリ骨格: Sidebar (二段) + TopBar + Main の 3 エリア構成.
// Notion 流に sidebar の collapse を localStorage 永続でサポート.
// スマホ幅 (xs) では sticky 列が本文 (録音ボタン等) を画面外へ押し出すため、
// 常時居座る列をやめて overlay Drawer 化する (論点D)。患者選択/録音開始は Drawer 内に
// 集約済なので、遷移 (= URL 変化) で Drawer を自動クローズすれば動線は保たれる。
export function Layout({
  children,
  breadcrumb,
  wide,
  renderSidebar = (props) => <Sidebar {...props} />,
}: LayoutProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  // AnyCOMPASS 埋め込み時はホスト側がナビ・患者バンドを持つため breadcrumb は出さず、
  // Sidebar (最近の薬歴) はホスト連携が患者を開かない場合の fallback 導線として
  // overlay Drawer に畳む。iframe のパネル幅はホスト側の裁量で狭くなり得るので、
  // 静的列で本文を圧迫しないようスマホ幅と同じ Drawer 扱いにする。
  // TopBar 行 (44px) はトグルしか載らず縦を浪費するため出さず、トグルだけを左上に
  // フローティングさせて main の上余白も詰める (= 埋め込み面積を本文に譲る引き算)。
  // 埋め込み時の PatientInfoBar は患者名を出さず左側が空くので、フローティングの
  // トグルと mic 側の pill 列が同じ帯に収まり 1 行として読める。
  const { isEmbedded } = useAnyCompassEmbed()
  // overlay Drawer で出す条件 (スマホ幅 / 埋め込み)。開閉は永続せず既定 closed。
  const useDrawer = isMobile || isEmbedded

  // デスクトップ (sm↑) の sticky 列の開閉. localStorage で永続。
  // 埋め込み時は Drawer 側の state を使い、standalone の開閉状態を汚さない。
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true
    const v = window.localStorage.getItem(SIDEBAR_OPEN_KEY)
    return v === null ? true : v === 'true'
  })
  // overlay Drawer の開閉. 永続せず既定 closed (本文を全幅にする)。
  const [drawerOpen, setDrawerOpen] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(SIDEBAR_OPEN_KEY, String(sidebarOpen))
  }, [sidebarOpen])

  const toggle = () => {
    if (useDrawer) setDrawerOpen((v) => !v)
    else setSidebarOpen((v) => !v)
  }
  const closeDrawer = () => setDrawerOpen(false)

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        background: colorTokens.bg.default,
        position: 'relative',
      }}
    >
      {useDrawer ? (
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={closeDrawer}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              // 幅は文字サイズモードに追従 (ADR-0019: 224 × fontScale, 丸め)
              width: (t) =>
                Math.round(LAYOUT.sidebarWidth * (t.fontScale ?? 1)),
              border: 'none',
            },
          }}
        >
          {renderSidebar({ onToggle: closeDrawer, onNavigate: closeDrawer })}
        </Drawer>
      ) : (
        sidebarOpen && renderSidebar({ onToggle: toggle })
      )}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        {isEmbedded ? (
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              left: 6,
              zIndex: (t) => t.zIndex.appBar,
            }}
          >
            <Tooltip title="サイドバーを開く">
              <IconButton
                size="small"
                onClick={toggle}
                sx={{ p: 0.5, backgroundColor: colorTokens.bg.default }}
              >
                <ChevronDoubleRightIcon sx={{ fontSize: ICON_SIZE.md }} />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <TopBar
            breadcrumb={breadcrumb}
            showSidebarToggle={useDrawer || !sidebarOpen}
            onToggleSidebar={toggle}
          />
        )}
        <Box
          component="main"
          sx={{
            flex: 1,
            padding: { xs: 2, sm: 3, md: 4 },
            ...(isEmbedded && { paddingTop: 1 }),
            overflow: 'auto',
          }}
        >
          <Box
            sx={{
              maxWidth: {
                xs: '100%',
                md: wide ? LAYOUT.contentMaxWide : LAYOUT.contentMax,
              },
              mx: 'auto',
              width: '100%',
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
